import React, { useEffect, useState } from 'react';
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withRepeat,
    withSequence,
    withTiming,
} from 'react-native-reanimated';
import {
    BodySmallRegular,
    FruitfulLogoIcon,
    useThemeColors,
    XStack,
    YStack,
} from 'smoothie';

export const CULTIVATING_FADE_MS = 600;
export const CULTIVATING_HOLD_MS = 3000;

const ELLIPSIS_TICK_MS = 400;
const ELLIPSIS_FRAMES = ['.', '..', '...'] as const;

const BOUNCE_CONFIG = { duration: 700, easing: Easing.inOut(Easing.ease) };

const BouncingLogo = () => {
    const { getColor } = useThemeColors();
    const bounceStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: withRepeat(
                    withSequence(
                        withTiming(-4, BOUNCE_CONFIG),
                        withTiming(4, BOUNCE_CONFIG),
                    ),
                    -1,
                    true,
                ),
            },
        ],
    }));

    return (
        <Animated.View style={bounceStyle}>
            <FruitfulLogoIcon
                width={48}
                height={41}
                color={getColor('$fruitfulGreen')}
            />
        </Animated.View>
    );
};

const CultivatingLabel = () => {
    const [dotCount, setDotCount] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setDotCount((prev) => (prev + 1) % ELLIPSIS_FRAMES.length);
        }, ELLIPSIS_TICK_MS);
        return () => {
            clearInterval(interval);
        };
    }, []);

    return (
        <XStack alignItems="center">
            <BodySmallRegular color="$fruitfulGreen" fontStyle="italic">
                Cultivating
            </BodySmallRegular>
            <BodySmallRegular
                color="$fruitfulGreen"
                fontStyle="italic"
                // Reserve space for "..." so the label doesn't shift as dots cycle.
                style={{ minWidth: 18 }}
            >
                {ELLIPSIS_FRAMES[dotCount]}
            </BodySmallRegular>
        </XStack>
    );
};

/**
 * Full-screen "Cultivating..." hold shown after chat fades out and before
 * the aha-moment hero fades in. Logo + text fade out together.
 */
export const CultivatingScreen = ({ visible }: { visible: boolean }) => {
    const opacity = useSharedValue(1);

    useEffect(() => {
        opacity.value = withTiming(visible ? 1 : 0, {
            duration: CULTIVATING_FADE_MS,
            easing: Easing.inOut(Easing.ease),
        });
    }, [visible, opacity]);

    const fadeStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
    }));

    return (
        <Animated.View
            pointerEvents={visible ? 'auto' : 'none'}
            style={[
                {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 5,
                    alignItems: 'center',
                    justifyContent: 'center',
                },
                fadeStyle,
            ]}
        >
            <YStack gap={8} alignItems="center" justifyContent="center">
                <BouncingLogo />
                <CultivatingLabel />
            </YStack>
        </Animated.View>
    );
};
